import axios from 'axios';

const CancelToken = axios.CancelToken;
let projectsSource;
let permissionsRequest;

/**
 * Bodhi API wraps payloads as { status, data }. Paginated routes nest a second
 * { data, pagination } and the key routes may pass Insight's envelope through,
 * so lists and objects are looked up at either depth.
 */
const unwrap = (response) => {
  const body = response && response.data;
  return body && body.data !== undefined ? body.data : body;
};

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.keys)) return payload.keys;
  if (payload && payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  return [];
};

const extractCreatedKey = (payload) => {
  if (payload && typeof payload.key === 'string') return payload;
  if (payload && payload.data && typeof payload.data.key === 'string') return payload.data;
  return null;
};

const responseData = (error) => {
  const body = error && error.response ? error.response.data : null;
  return body && typeof body === 'object' ? body : null;
};

export const errorStatus = (error) => (error && error.response ? error.response.status : null);

export const errorMessage = (error) => {
  const body = responseData(error);
  if (!body) return null;
  if (typeof body.message === 'string') return body.message;
  if (body.error && typeof body.error.message === 'string') return body.error.message;
  return null;
};

// Error bodies are flat: { name, status, message, code }, plus `enabled` on a sync revert.
export const errorCode = (error) => {
  const body = responseData(error);
  if (!body) return null;
  if (typeof body.code === 'string') return body.code;
  if (body.error && typeof body.error.code === 'string') return body.error.code;
  return null;
};

export const errorEnabled = (error) => {
  const body = responseData(error);
  if (!body) return null;
  if (typeof body.enabled === 'boolean') return body.enabled;
  if (body.error && typeof body.error.enabled === 'boolean') return body.error.enabled;
  return null;
};

const state = {
  permissions: {
    loaded: false,
    error: false,
    view: false,
    manage: false
  }
};

const mutations = {
  SET_PERMISSIONS: (state, { view, manage }) => {
    state.permissions = {
      loaded: true,
      error: false,
      view: view === true,
      manage: manage === true
    };
  },
  SET_PERMISSIONS_ERROR: (state) => {
    // loaded stays false so the next getPermissions call retries (do not cache a deny-all).
    state.permissions = {
      loaded: false,
      error: true,
      view: false,
      manage: false
    };
  }
};

const actions = {
  getPermissions: ({ commit, state }, { force } = {}) => {
    if (state.permissions.loaded && force !== true && state.permissions.error !== true) {
      return Promise.resolve(state.permissions);
    }
    if (permissionsRequest) {
      return permissionsRequest;
    }

    permissionsRequest = axios.get('/client_data_api/permissions')
      .then(response => {
        const data = unwrap(response) || {};
        commit('SET_PERMISSIONS', data);
        return state.permissions;
      })
      .catch(error => {
        const status = error && error.response && error.response.status;
        console.error(`[ClientDataApi] permissions failed status=${status || '-'} message=${(error && error.message) || 'request failed'}`);
        commit('SET_PERMISSIONS_ERROR');
        return state.permissions;
      })
      .finally(() => { permissionsRequest = undefined; });

    return permissionsRequest;
  },

  getProjects: (context, { search, page }) => {
    if (projectsSource) {
      projectsSource.cancel();
    }
    projectsSource = CancelToken.source();

    const params = { page };
    if (search) params.search = search;

    return axios.get('/client_data_api/projects', { params, cancelToken: projectsSource.token })
      .then(response => {
        projectsSource = undefined;
        const payload = unwrap(response);
        return {
          data: extractList(payload),
          pagination: (payload && payload.pagination) || null
        };
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
        return null;
      });
  },

  setProjectEnabled: (context, { projectId, enabled }) => {
    return axios.put(`/client_data_api/projects/${projectId}/enabled`, { enabled })
      .then(response => unwrap(response));
  },

  getKeys: (context, projectUid) => {
    return axios.get(`/client_data_api/projects/${encodeURIComponent(projectUid)}/keys`)
      .then(response => extractList(unwrap(response)));
  },

  createKey: (context, { projectUid, label, expiresAt }) => {
    const body = { expires_at: expiresAt };
    if (label) body.label = label;

    return axios.post(`/client_data_api/projects/${encodeURIComponent(projectUid)}/keys`, body)
      .then(response => extractCreatedKey(unwrap(response)));
  },

  revokeKey: (context, { projectUid, keyId }) => {
    return axios.delete(`/client_data_api/projects/${encodeURIComponent(projectUid)}/keys/${keyId}`)
      .then(response => unwrap(response));
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
